import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteDungeon, DungeonFilterParams, getDungeonById, saveDungeonBasics, saveDungeonGeometry, searchDungeons} from "@/api/DungeonApi.ts";
import type {SaveDungeonGeometryRequest} from "@/dungeons/admin/form/DungeonFormSchema.ts";

export const dungeonKeys = {
    all: ["dungeons"] as const,
    lists: () => [...dungeonKeys.all, "list"] as const,
    list: (filter: DungeonFilterParams, pageSize: number) => [...dungeonKeys.lists(), filter, pageSize] as const,
    detail: (id: string) => [...dungeonKeys.all, "detail", id] as const,
};

export function useDungeonQuery(id?: string) {
    return useQuery({
        queryKey: dungeonKeys.detail(id ?? ""),
        queryFn: ({signal}) => getDungeonById(id!, signal),
        enabled: Boolean(id),
    });
}

export function useDungeonsQuery(filter: DungeonFilterParams, pageSize = 50) {
    const query = useInfiniteQuery({
        queryKey: dungeonKeys.list(filter, pageSize),
        initialPageParam: 0,
        queryFn: ({pageParam, signal}) => searchDungeons({page: pageParam, size: pageSize}, filter, signal),
        getNextPageParam: lastPage => {
            const nextPage = lastPage.page.number + 1;
            return nextPage < lastPage.page.totalPages ? nextPage : undefined;
        },
    });
    const dungeons = query.data?.pages.flatMap(page => page.content) ?? [];
    return {
        ...query,
        dungeons,
        totalCount: query.data?.pages[0]?.page.totalElements ?? 0,
        rowCount: dungeons.length + (query.hasNextPage ? 1 : 0),
        loadMore: async () => {
            if (query.hasNextPage) await query.fetchNextPage({cancelRefetch: false});
        },
    };
}

export function useSaveDungeonBasicsMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: saveDungeonBasics,
        onSuccess: dungeon => {
            queryClient.setQueryData(dungeonKeys.detail(dungeon.id), dungeon);
            void queryClient.invalidateQueries({queryKey: dungeonKeys.lists()});
        },
    });
}

export function useSaveDungeonGeometryMutation(id: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: SaveDungeonGeometryRequest) => saveDungeonGeometry(id, request),
        onSuccess: dungeon => {
            queryClient.setQueryData(dungeonKeys.detail(dungeon.id), dungeon);
            void queryClient.invalidateQueries({queryKey: dungeonKeys.lists()});
        },
    });
}

export function useDeleteDungeonMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteDungeon,
        onSuccess: (_, id) => {
            queryClient.removeQueries({queryKey: dungeonKeys.detail(id)});
            void queryClient.invalidateQueries({queryKey: dungeonKeys.lists()});
        },
    });
}
